import { createSlice } from '@reduxjs/toolkit';
import { createAsyncThunkWithLoggingAndRetry } from '../../../store/middleware/thunk_helper';
import { PENDING, REQUESTING, SUCCESS, FAILED } from '../../../actions/statusTypes';
import * as types from '../../../actions/actionTypes';
import { BASE_URL, env } from '../../../constants/env';

import AuthService from '../../../services/auth_service';

export const resetOktaTokenForUser = createAsyncThunkWithLoggingAndRetry(
  'okta/resetOktaTokenForUser',
  ({ userId, employerId }) => {
    return AuthService.resetOktaTokenForUser(userId, employerId);
  }
);

export const getMyUsersByOktaToken = createAsyncThunkWithLoggingAndRetry(
  'okta/getMyUsersByOktaToken',
  () => {
    return AuthService.getUsersByOktaToken();
  }
);

export const requestEmailLinkForOktaAccountLink = createAsyncThunkWithLoggingAndRetry(
  'okta/requestEmailLinkForOktaAccountLink',
  ({ email }) => {
    return AuthService.requestEmailLinkForOktaAccountLink(email);
  }
);

const _linkMyAccountWithOktaByGuid = createAsyncThunkWithLoggingAndRetry(
  'okta/linkMyAccountWithOktaByGuid',
  ({ guid }) => {
    return AuthService.linkMyAccountWithOktaByGuid(guid);
  }
);

const _acceptInviteWithOktaByGuid = createAsyncThunkWithLoggingAndRetry(
  'okta/acceptInviteWithOktaByGuid',
  ({ guid, locale }) => {
    return AuthService.acceptInviteWithOktaByGuid(guid, { locale });
  }
);

const _loginUserWithUserId = createAsyncThunkWithLoggingAndRetry(
  'okta/loginUserWithUserId',
  ({ userId }) => {
    return AuthService.getUserById(userId).then((userData) => {
      return {
        ...userData,
        isMilitary: true,
        afTermsAlreadyAccepted: true,
      };
    });
  }
);

export const loginWithUserId = ({ userId }) => {
  return async (dispatch, getState) => {
    dispatch({ type: types.SET_BASE_URL, payload: env(BASE_URL) });
    dispatch({ type: types.FORCE_FULL_SYNC });
    await dispatch(_loginUserWithUserId({ userId }));
  };
};

export const linkMyAccountWithOktaByGuidAndLogin = ({ guid }) => {
  return async (dispatch, getState) => {
    await dispatch(_linkMyAccountWithOktaByGuid({ guid }));
    const userId = getState().screens.oktaAuth.loginUserId;
    if (userId) {
      await dispatch(loginWithUserId({ userId }));
    }
  };
};

export const findMyUsersAndSignIn = () => {
  return async (dispatch, getState) => {
    await dispatch(getMyUsersByOktaToken());
    const users = getState().screens.oktaAuth.users;
    if (users?.length === 1 && !getState().application.wasLoggedIn) {
      dispatch(loginWithUserId({ userId: users[0].userId }));
    }
  };
};

export const acceptInviteByGuid = ({ guid }) => {
  return async (dispatch, getState) => {
    await dispatch(_acceptInviteWithOktaByGuid({ guid, locale: getState().application.locale }));
    const userId = getState().screens.oktaAuth.loginUserId;
    if (userId) {
      await dispatch(loginWithUserId({ userId }));
    }
  };
};

const INITIAL_STATE = {
  getUsersStatus: PENDING,
  getUsersError: null,
  users: [],
  requestEmailLinkStatus: PENDING,
  requestEmailLinkError: null,
  linkMyAccountStatus: PENDING,
  linkMyAccountError: null,
  loginUserStatus: PENDING,
  loginUserError: null,
  loginUserId: null,
  acceptInviteStatus: PENDING,
  acceptInviteError: null,
  resetOktaTokenStatus: PENDING,
  resetOktaTokenError: null,
};

const oktaSlice = createSlice({
  name: 'okta',
  initialState: INITIAL_STATE,
  reducers: {
    resetState: (state) => {
      return {
        ...INITIAL_STATE,
      };
    },
    oktaAuthSuccess: (state, action) => {
      return state;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(resetOktaTokenForUser.pending, (state) => {
        state.resetOktaTokenStatus = REQUESTING;
        state.resetOktaTokenError = null;
      })
      .addCase(resetOktaTokenForUser.fulfilled, (state, action) => {
        state.resetOktaTokenStatus = SUCCESS;
      })
      .addCase(resetOktaTokenForUser.rejected, (state, action) => {
        state.resetOktaTokenStatus = FAILED;
        state.resetOktaTokenError = action.payload;
      })
      .addCase(_acceptInviteWithOktaByGuid.pending, (state) => {
        state.acceptInviteStatus = REQUESTING;
        state.acceptInviteError = null;
      })
      .addCase(_acceptInviteWithOktaByGuid.fulfilled, (state, action) => {
        state.acceptInviteStatus = SUCCESS;
        state.loginUserId = action.payload.userId;
      })
      .addCase(_acceptInviteWithOktaByGuid.rejected, (state, action) => {
        state.acceptInviteStatus = FAILED;
        state.acceptInviteError = action.payload;
      })
      .addCase(_loginUserWithUserId.pending, (state) => {
        state.loginUserStatus = REQUESTING;
        state.loginUserError = null;
      })
      .addCase(_loginUserWithUserId.fulfilled, (state, action) => {
        state.loginUserStatus = SUCCESS;
      })
      .addCase(_loginUserWithUserId.rejected, (state, action) => {
        state.loginUserStatus = FAILED;
        state.loginUserError = action.payload;
      })
      .addCase(_linkMyAccountWithOktaByGuid.pending, (state) => {
        state.linkMyAccountStatus = REQUESTING;
        state.linkMyAccountError = null;
      })
      .addCase(_linkMyAccountWithOktaByGuid.fulfilled, (state, action) => {
        state.linkMyAccountStatus = SUCCESS;
        state.loginUserId = action.payload.userId;
      })
      .addCase(_linkMyAccountWithOktaByGuid.rejected, (state, action) => {
        state.linkMyAccountStatus = FAILED;
        state.linkMyAccountError = action.payload;
      })
      .addCase(requestEmailLinkForOktaAccountLink.pending, (state) => {
        state.requestEmailLinkStatus = REQUESTING;
        state.requestEmailLinkError = null;
      })
      .addCase(requestEmailLinkForOktaAccountLink.fulfilled, (state, action) => {
        state.requestEmailLinkStatus = SUCCESS;
      })
      .addCase(requestEmailLinkForOktaAccountLink.rejected, (state, action) => {
        state.requestEmailLinkStatus = FAILED;
        state.requestEmailLinkError = action.payload;
      })
      .addCase(getMyUsersByOktaToken.pending, (state) => {
        state.getUsersStatus = REQUESTING;
        state.getUsersError = null;
        state.users = [];
      })
      .addCase(getMyUsersByOktaToken.fulfilled, (state, action) => {
        state.getUsersStatus = SUCCESS;
        state.users = action.payload;
        // test one user case
        // state.users = action.payload.slice(0, 1);
      })
      .addCase(getMyUsersByOktaToken.rejected, (state, action) => {
        state.getUsersStatus = FAILED;
        state.getUsersError = action.payload;
      });
  },
});

export const { resetState, oktaAuthSuccess } = oktaSlice.actions;
export default oktaSlice.reducer;
